/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Stack } from "@mui/material";
import React, { type JSX } from "react";

import TestIds from "../../../../testIds";
import { Activity, Lookup } from "../../api/types/types";
import { sortActivitiesByCompletionAndDate } from "../../utils/activitySorting";

import ActivitiesListLoadingDisplay from "./ActivitiesListLoadingDisplay";
import ActivityListItem from "./ActivityListItem";
import NoActivities from "./NoActivities";

export interface ActivitiesListProps {
  activities?: Activity[];
  lookups?: Lookup[];
  onActivityClick?: (activity: Activity) => void;
  loading: boolean;
}

const ActivitiesList: React.FC<ActivitiesListProps> = ({
  activities,
  lookups = [],
  onActivityClick,
  loading,
}): JSX.Element => {
  if (loading) {
    return <ActivitiesListLoadingDisplay />;
  }

  const sortedActivities = sortActivitiesByCompletionAndDate(activities || []);
  return (
    <Stack
      flex={1}
      flexDirection="row"
      data-testid={TestIds.feat.activities.activitiesList.component}
    >
      {sortedActivities.length === 0 ? (
        <NoActivities />
      ) : (
        <Stack gap={2} flex={1} alignItems="stretch">
          {sortedActivities.map((activity) => (
            <ActivityListItem
              key={activity.activityId}
              activity={activity}
              lookups={lookups}
              onClick={onActivityClick}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default ActivitiesList;
